import { ReadFile } from '@paragon/client-tools';
import {
  AnalysisSetGuidWithQuantity,
  AnalysisSetsWithInformation,
  BidData,
  ComponentDesign,
  EstimateData,
  ProjectSnapshot,
  SharedComponentSet,
} from '@paragon/contract-library';

import { DesignService } from '../connections';

export function fetch(lookupCode: string) {
  return DesignService.get<SharedComponentSet>(`sharedComponentSets/${lookupCode}`);
}

export function fetchSamples() {
  return DesignService.get<readonly SharedComponentSet[]>(`sharedComponentSets/samples`);
}

export function fetchForProject(projectGuid: string) {
  return DesignService.get<readonly SharedComponentSet[]>(`sharedComponentSets/forProject/${projectGuid}`);
}

export function fetchAnalysisSetsWithInformation(lookupCode: string) {
  return DesignService.get<AnalysisSetsWithInformation>(
    `sharedComponentSets/${lookupCode}/getAnalysisSetsWithInformation`,
  );
}

export function fetchProjectSnapshot(lookupCode: string) {
  return DesignService.get<ProjectSnapshot>(`sharedComponentSets/${lookupCode}/projectSnapshot`);
}

export function share(
  lookupCode: string,
  fromEmail: string | null,
  toEmailAddresses: readonly string[],
  subject: string,
  message: string | null,
  files: readonly ReadFile[],
) {
  return DesignService.post<void>(`sharedComponentSets/${lookupCode}/share`, {
    data: {
      fromEmail,
      toEmailAddresses,
      subject,
      message,
      files: files.map((file) => ({
        name: `${file.filename}.${file.filetype}`,
        base64Contents: file.contents.split(',')[1],
      })),
    },
  });
}

export function create(
  name: string,
  analysisSetGuidsWithQuantities: readonly AnalysisSetGuidWithQuantity[],
  componentDesignGuidsToIncludeInSnapshot: readonly string[],
  sourceProjectGuid: string | null,
  shareProjectSnapshot: boolean,
) {
  return DesignService.post<SharedComponentSet>(`sharedComponentSets`, {
    data: {
      name,
      analysisSetGuidsWithQuantities,
      componentDesignGuidsToIncludeInSnapshot,
      sourceProjectGuid,
      shareProjectSnapshot,
    },
  });
}

interface InsertComponentDesignsFromLookupCodeResponse {
  readonly componentDesignsWithUpdatedGuids: readonly ComponentDesign[];
  readonly newComponentDesignGuidToQuantity: { readonly [newComponentDesignGuid: string]: number };
  readonly sharedComponentSetName: string;
}

export function insertComponentDesignsFromLookupCode(sharedComponentSetLookupCode: string) {
  return DesignService.post<InsertComponentDesignsFromLookupCodeResponse>(
    `sharedComponentSets/insertComponentDesignsFromLookupCode/${sharedComponentSetLookupCode}`,
  );
}

export function copyToMyOrganization(lookupCode: string) {
  return DesignService.post<string>(`sharedComponentSets/${lookupCode}/copyToMyOrganization`);
}

export type SharedFileDownloadTypes = 'LayoutDxf' | 'LayoutIfc' | 'LayoutPdf';

export function downloadFiles(
  lookupCode: string,
  fileType: SharedFileDownloadTypes,
  params?: URLSearchParams,
  data?: string | object | null,
) {
  return DesignService.fetchFile(`sharedComponentSets/${lookupCode}/downloadFiles/${fileType}`, {
    post: true,
    params,
    data,
  });
}

export function getEstimateData(
  lookupCode: string,
  analysisSetGuidToQuantity: { readonly [analysisSetGuid: string]: number },
) {
  return DesignService.post<EstimateData>(`sharedComponentSets/${lookupCode}/estimateData`, {
    data: analysisSetGuidToQuantity,
  });
}

export function getBidPdf(bidData: BidData) {
  return DesignService.fetchFile('sharedComponentSets/bidPdf', { post: true, data: bidData });
}
